import React from "react";
import { Link } from "react-router-dom";
import { useCart } from "../lib/cart";
import { Plus, Minus, WarningCircle } from "@phosphor-icons/react";
import { formatGBP } from "../lib/format";
import { toast } from "sonner";

export default function ProductCard({ product, compact = false }) {
  const { add, items, updateQty } = useCart();
  const inCart = items.find((i) => i.product_id === product.product_id);
  const outOfStock = product.stock_quantity <= 0;
  const lowStock = !outOfStock && product.stock_quantity <= (product.low_stock_threshold || 5);

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (outOfStock) return;
    add(product, 1);
    toast.success(`${product.name} added to cart`);
  };

  const inc = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (inCart && inCart.quantity < product.stock_quantity) {
      updateQty(product.product_id, inCart.quantity + 1);
    }
  };
  const dec = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (inCart) updateQty(product.product_id, inCart.quantity - 1);
  };

  return (
    <Link
      to={`/products/${product.product_id}`}
      data-testid="product-card"
      className="group relative flex flex-col bg-white rounded-2xl border border-[#E5E2D9] overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_36px_rgba(0,0,0,0.08)] hover:border-[#D1CEC5]"
    >
      <div className="relative w-full aspect-square bg-[#F2EFE9] overflow-hidden">
        <img
          src={product.images?.[0]}
          alt={product.name}
          loading="lazy"
          onError={(e) => { e.currentTarget.src = "https://images.pexels.com/photos/37099829/pexels-photo-37099829.jpeg"; }}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        />
        {outOfStock && (
          <div className="absolute inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center">
            <span className="chip font-bold">Out of stock</span>
          </div>
        )}
        {lowStock && (
          <span className="absolute top-3 left-3 chip-warning">
            <WarningCircle size={12} weight="fill" /> Only {product.stock_quantity} left
          </span>
        )}
        {product.is_featured && (
          <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-[#A94438] text-white text-[10px] font-bold tracking-wide uppercase">Popular</span>
        )}
      </div>

      <div className="p-4 flex-1 flex flex-col">
        <p className="text-[10px] font-bold tracking-wider text-[#5C5C54] uppercase mb-1">{product.vendor_name}</p>
        <h3 className={`font-heading font-bold ${compact ? "text-sm" : "text-base"} tracking-tight leading-tight mb-2 line-clamp-2`}>
          {product.name}
        </h3>
        <div className="mt-auto flex items-end justify-between gap-2">
          <span className="font-heading font-black text-lg text-[#1A1A18]">{formatGBP(product.price)}</span>

          {inCart ? (
            <div className="flex items-center gap-2" data-testid="product-qty-controls">
              <button
                onClick={dec}
                data-testid="qty-decrement"
                className="w-8 h-8 rounded-full bg-[#F2EFE9] border border-[#E5E2D9] flex items-center justify-center hover:bg-[#E5E2D9]"
              >
                <Minus size={14} weight="bold" />
              </button>
              <span className="text-sm font-bold w-6 text-center" data-testid="qty-value">{inCart.quantity}</span>
              <button
                onClick={inc}
                data-testid="qty-increment"
                disabled={inCart.quantity >= product.stock_quantity}
                className="w-8 h-8 rounded-full bg-[#1A1A18] text-white flex items-center justify-center hover:bg-[#2B5329] disabled:opacity-50"
              >
                <Plus size={14} weight="bold" />
              </button>
            </div>
          ) : (
            <button
              onClick={handleAdd}
              disabled={outOfStock}
              data-testid="add-to-cart-btn"
              className="w-10 h-10 rounded-full bg-[#1A1A18] text-white flex items-center justify-center hover:bg-[#A94438] active:scale-95 transition-all disabled:opacity-40"
            >
              <Plus size={18} weight="bold" />
            </button>
          )}
        </div>
      </div>
    </Link>
  );
}
